import type {
  AuthenticationObject,
  GetConfigOutput,
} from '@knitiv/api-client-javascript'
import * as Sentry from "@sentry/browser"
import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { EngineVersion } from '@/middleware/auth'

export const useConfig = defineStore('config', () => {
  const endpoint = ref('/')
  const page = ref('/')
  const user = ref<AuthenticationObject | null>(null)
  const logged = ref(false)
  const isAktivnote = ref(false)
  const isEmbedded = ref(import.meta.env.VITE_FRONT5_BASE_URL !== '/')
  const isMobile = ref(import.meta.env.VITE_IS_MOBILE === '1')
  const buildReference = ref(import.meta.env.BUILD_REFERENCE)

  // Change application mode for custom pages like /importfile
  const mode = ref<EngineVersion>(5)

  const hideNavBar = ref(true)
  const allowNavigatingTo = ref<string[]>([])

  const config = ref<GetConfigOutput['config']>()

  const setPage = (_page: string) => {
    page.value = _page
  }

  const setUser = (_user: AuthenticationObject | null) => {
    user.value = _user

    if (_user) {
      Sentry.setContext("user", user.value)
      Sentry.setUser({ email: user.value?.profil.email, id: user.value?.profil.kid, username: user.value?.profil.name })
    }
  }

  const setMode = (_mode: EngineVersion) => {
    mode.value = _mode
  }

  const setHideNavbar = (_hide: boolean) => {
    hideNavBar.value = _hide
  }

  const setAllowNavigatingTo = (_pages: string[]) => {
    allowNavigatingTo.value = _pages
  }

  const setLogged = (_logged: boolean) => {
    logged.value = _logged
  }

  const setConfig = (_config: GetConfigOutput['config']) => {
    config.value = _config
  }

  const setAktivnote = (_isAktivnote: boolean) => {
    isAktivnote.value = _isAktivnote
  }

  return {
    endpoint,
    page,
    user,
    mode,
    logged,
    hideNavBar,
    allowNavigatingTo,
    config,
    isAktivnote,
    isEmbedded,
    isMobile,
    buildReference,

    setPage,
    setUser,
    setLogged,
    setMode,
    setHideNavbar,
    setAllowNavigatingTo,
    setConfig,
    setAktivnote,
  }
})

export type ConfigState = ReturnType<typeof useConfig>
